// Cliente del API multi-tenant /api/v2 para el POS (terminal).
//
// Sesión de TERMINAL, no de usuario: el POS web se empareja con un código igual que la app
// móvil, y guarda un `device_token`. No hay PIN — v2 no tiene el concepto. El tenant NUNCA
// lo impone el cliente: sale del token.
//
// Distinto de `adminApi.ts`, que usa JWT de usuario + X-Tenant-Id para el panel del manager.
// Son dos sesiones independientes a propósito: en un mostrador la terminal queda logueada y
// el dueño entra al panel desde otro lado.

const BASE = "/api/v2";
const TOKEN_KEY = "lpos.device.token";
const INFO_KEY = "lpos.device.info";

export interface DeviceTerminal {
  id: string;
  name: string;
  role: "cashier" | "manager";
}

export interface DeviceTenant {
  id: string;
  name: string;
}

export interface DeviceSession {
  token: string;
  terminal: DeviceTerminal;
  tenant: DeviceTenant;
}

/** Lo que este comercio puede cobrar DE VERDAD. Fuente de verdad del backend: si su wallet
 *  no está conectada, `lightning` no aparece y el botón no se ofrece. */
export type PaymentMethod = "lightning" | "cash";

export interface TerminalMe {
  terminal_id: string;
  name: string;
  role: string;
  tenant_id: string;
  payment_methods: PaymentMethod[];
}

export interface DeviceProduct {
  id: string;
  name: string;
  price_mxn: string;
  barcode: string | null;
  active: boolean;
}

export interface DeviceOrder {
  id: string;
  status: string;
  subtotal_mxn: string;
  discount_mxn: string;
  total_mxn: string;
  created_at: string;
  payment_method: PaymentMethod;
  paid_at: string | null;
}

export interface DeviceInvoice {
  id: string;
  order_id: string;
  status: "pending" | "paid" | "expired" | "cancelled";
  amount_sats: number;
  amount_mxn: string;
  fx_rate: string;
  bolt11: string;
  payment_hash: string;
  expires_at: string;
  paid_at: string | null;
}

export interface DeviceSaleItem {
  /** null = línea libre (propina, ítem suelto), no un producto del catálogo. */
  product_id: string | null;
  description: string;
  qty: number;
  unit_price_mxn: string;
  line_total_mxn: string;
}

export interface DeviceSale {
  order_id: string;
  status: string;
  payment_method: PaymentMethod;
  total_mxn: string;
  paid_at: string | null;
  created_at: string;
  invoice_id: string | null;
  amount_sats: number | null;
  payment_hash: string | null;
  fx_rate: string | null;
  expires_at: string | null;
  items: DeviceSaleItem[];
  discount_mxn: string;
}

export type OrderItemIn =
  | { product_id: string; qty: number }
  | { description: string; qty: number; unit_price_mxn: string };

/** Error con el `detail` del backend intacto.
 *
 *  El código importa: la UI distingue `lightning_unavailable` (el proveedor de pagos está
 *  caído, NO es culpa de la app) de un fallo nuestro. Aplastarlo a un string suelto fue
 *  exactamente lo que hacía ilegible el "502: cuerpo crudo" de v1. */
export class DeviceApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string) {
    super(code);
    this.status = status;
    this.code = code;
  }
}

export function getDeviceSession(): DeviceSession | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const raw = localStorage.getItem(INFO_KEY);
    if (!token || !raw) return null;
    const info = JSON.parse(raw);
    return { token, terminal: info.terminal, tenant: info.tenant };
  } catch {
    return null;
  }
}

export function setDeviceSession(
  token: string,
  terminal: DeviceTerminal,
  tenant: DeviceTenant
): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(INFO_KEY, JSON.stringify({ terminal, tenant }));
  } catch {
    // storage puede no estar disponible (modo privado): la sesión vive en memoria y punto.
  }
}

export function clearDeviceSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(INFO_KEY);
  } catch {
    /* noop */
  }
}

/** El token fue revocado desde el panel (o expiró). Sacar al cajero a la pantalla de pareo:
 *  seguir mostrando una UI que ya no puede cobrar es peor que mandarlo a re-emparejar. */
function onUnauthorized(): void {
  clearDeviceSession();
  window.location.replace("/");
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  /** El canje del código corre SIN sesión: es lo que la crea. */
  anonymous?: boolean;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (!opts.anonymous) {
    const session = getDeviceSession();
    if (session) headers["Authorization"] = `Bearer ${session.token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new DeviceApiError(0, "network_error");
  }

  if (res.status === 401 && !opts.anonymous) {
    onUnauthorized();
    throw new DeviceApiError(401, "unauthorized");
  }
  if (!res.ok) {
    let code = `http_${res.status}`;
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") code = data.detail;
    } catch {
      /* cuerpo no-JSON: queda el código genérico */
    }
    throw new DeviceApiError(res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const deviceApi = {
  /** Canjea el código de pareo del panel. Devuelve el device token. */
  redeem: (code: string, deviceName?: string) =>
    request<{ device_token: string; terminal: DeviceTerminal; tenant: DeviceTenant }>(
      "/pairing/redeem",
      { method: "POST", body: { code, device_name: deviceName }, anonymous: true }
    ),
  me: () => request<TerminalMe>("/terminal/me"),
  products: {
    list: () => request<DeviceProduct[]>("/catalog/products?limit=200"),
  },
  orders: {
    create: (items: OrderItemIn[], discountMxn = "0") =>
      request<DeviceOrder>("/orders", {
        method: "POST",
        body: { items, discount_mxn: discountMxn },
      }),
    invoice: (orderId: string) =>
      request<DeviceInvoice>(`/orders/${orderId}/invoice`, { method: "POST" }),
    cash: (orderId: string) =>
      request<DeviceOrder>(`/orders/${orderId}/cash`, { method: "POST" }),
    get: (orderId: string) => request<DeviceOrder>(`/orders/${orderId}`),
  },
  invoices: {
    get: (invoiceId: string) => request<DeviceInvoice>(`/invoices/${invoiceId}`),
  },
  sales: {
    list: (limit = 200) => request<DeviceSale[]>(`/sales?limit=${limit}`),
  },
};
